import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Field, FieldMetaState, Form } from 'react-final-form';

import { RootState } from '../../../../store';
import {
    AcademicDegree,
    ExternalSupervisorInvitation,
    User,
} from '../../../../store/types';
import { UploadFile } from '../../../shared/UploadFile/UploadFile';
import {
    ACADEMIC_DEGREE_PROOF_FILE_SIZE_LIMIT,
    acceptPdf,
    DropZoneType,
} from '../../../shared/UploadFile/uploadFileConstants';
import { INITIAL_USER_STATE, generatePublicFileUrl } from '../../../../utils';
import { downloadFile } from '../../../../services/downloadFile';
import { AppSelect } from '../../../shared/Select';
import { stringFormat } from 'susy-common/stringUtils';
import SusyButton from '../../../shared/buttons/SusyButton';
import { AuthPageWrapper } from '../../Wrapper/authPageWrapper/AuthPageWrapper';
import SusyFormField from '../../../shared/forms/SusyFormField/SusyFormField';
import { externalSupervisorSignUp } from '../../../../api/supervisor';
import SusyCheckbox from '../../../shared/forms/SusyCheckbox';

import ValidationUtils from 'susy-common/validation-utils';

import './SupervisorSignUpForm.css';

type Props = {
    invitation: ExternalSupervisorInvitation;
};

export const SupervisorSignUpForm = (props: Props) => {
    const [academicDegreeProof, setAcademicDegreeProof] = useState<File | null>(
        null
    );
    const [isSignUpCompleted, setIsSignUpCompleted] = useState<boolean>(false);

    const localization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.authentication
                .supervisorSignUp
    );

    const signUp = async (values: FormValues) => {
        const newUser: User = {
            ...INITIAL_USER_STATE,
            intakes: [],
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.eMail.trim(),
            profile: {
                phone: values.phone,
                currentOccupation: values.currentOccupation.trim(),
                employer: values.employer.trim(),
                academicDegree: values.academicDegree.value,
            },
        };
        await externalSupervisorSignUp(
            newUser,
            props.invitation?._id,
            academicDegreeProof
        );
        setIsSignUpCompleted(true);
    };

    const renderErrorMessage = (meta: FieldMetaState<any>): JSX.Element => {
        return (
            <>
                {meta.error && meta.touched && (
                    <span className='asterisk'>{`* ${meta.error}`}</span>
                )}
            </>
        );
    };

    const getAcademicDegreeOptions = () => {
        return Object.values(AcademicDegree).map((degree) => ({
            label: degree,
            value: degree,
        }));
    };

    const onDrop = (files: Array<File>, rejected: any) => {
        if (!Object.keys(rejected).length && files?.length === 1) {
            setAcademicDegreeProof(files[0]);
        }
    };

    const onRemove = () => {
        setAcademicDegreeProof(null);
    };

    const setProofFile = () => {
        return academicDegreeProof
            ? new Map().set(1, academicDegreeProof)
            : new Map();
    };

    const subtitle = stringFormat(localization.subtitle, [
        localization.youAreAboutToAccept,
        props.invitation.project.title,
        localization.asYourSupervision,
    ]);

    const renderForm = () => {
        return (
            <AuthPageWrapper
                dataTestId={'supervisor-sign-up-form'}
                title={localization.title}
                subtitle={subtitle}
                wideForm
            >
                <Form
                    onSubmit={signUp}
                    validate={(values) => {
                        const errors: FormValueErrors = {
                            firstName: undefined,
                            lastName: undefined,
                            eMail: undefined,
                            phone: undefined,
                            currentOccupation: undefined,
                            employer: undefined,
                            academicDegree: undefined,
                            degreeConfirmation: undefined,
                        };

                        if (!values.firstName) {
                            errors.firstName = localization.errorFirstName;
                        }
                        if (!values.lastName) {
                            errors.lastName = localization.errorLastName;
                        }
                        if (
                            !values.eMail ||
                            !ValidationUtils.isEmailValid(values.eMail)
                        ) {
                            errors.eMail = localization.errorEmail;
                        }
                        if (!values.phone) {
                            errors.phone = localization.errorPhone;
                        }
                        if (!values.currentOccupation) {
                            errors.currentOccupation =
                                localization.errorCurrentOccupation;
                        }
                        if (!values.employer) {
                            errors.employer = localization.errorEmployer;
                        }
                        if (!values.academicDegree) {
                            errors.academicDegree =
                                localization.errorAcademicDegree;
                        }
                        if (!values.degreeConfirmation) {
                            errors.degreeConfirmation =
                                localization.errorDegreeConfirmation;
                        }

                        return errors;
                    }}
                    render={({ handleSubmit, values }) => (
                        <form onSubmit={handleSubmit} className='mt-3'>
                            {/* First name and last name */}
                            <Row>
                                <Col className='display-grid'>
                                    <SusyFormField
                                        required
                                        label={{
                                            fieldIdentifier: `firstName`,
                                            text: localization.labelFirstName,
                                        }}
                                    >
                                        <Field name='firstName'>
                                            {(fieldProps) => (
                                                <div>
                                                    <input
                                                        data-testid='first-name-input'
                                                        className='form-control'
                                                        placeholder={
                                                            localization.namePlaceholder
                                                        }
                                                        {...fieldProps.input}
                                                    />
                                                    {renderErrorMessage(
                                                        fieldProps.meta
                                                    )}
                                                </div>
                                            )}
                                        </Field>
                                    </SusyFormField>
                                </Col>
                                <Col className='display-grid'>
                                    <SusyFormField
                                        required
                                        label={{
                                            fieldIdentifier: `lastName`,
                                            text: localization.labelLastName,
                                        }}
                                    >
                                        <Field
                                            name='lastName'
                                            component='input'
                                            type='text'
                                        >
                                            {(fieldProps) => (
                                                <div>
                                                    <input
                                                        data-testid='last-name-input'
                                                        className='form-control'
                                                        placeholder={
                                                            localization.lastNamePlaceholder
                                                        }
                                                        {...fieldProps.input}
                                                    />
                                                    {renderErrorMessage(
                                                        fieldProps.meta
                                                    )}
                                                </div>
                                            )}
                                        </Field>
                                    </SusyFormField>
                                </Col>
                            </Row>

                            {/* Email and phone */}
                            <Row>
                                <Col className='display-grid'>
                                    <SusyFormField
                                        required
                                        label={{
                                            fieldIdentifier: `eMail`,
                                            text: localization.labelEmail,
                                        }}
                                    >
                                        <Field
                                            name='eMail'
                                            component='input'
                                            type='text'
                                        >
                                            {(fieldProps) => (
                                                <div>
                                                    <input
                                                        data-testid='email-input'
                                                        className='form-control'
                                                        placeholder={
                                                            localization.emailPlaceholder
                                                        }
                                                        {...fieldProps.input}
                                                    />
                                                    {renderErrorMessage(
                                                        fieldProps.meta
                                                    )}
                                                </div>
                                            )}
                                        </Field>
                                    </SusyFormField>
                                </Col>
                                <Col className='display-grid'>
                                    <SusyFormField
                                        required
                                        label={{
                                            fieldIdentifier: `phone`,
                                            text: localization.labelPhoneNumber,
                                        }}
                                    >
                                        <Field
                                            name='phone'
                                            component='input'
                                            type='text'
                                        >
                                            {(fieldProps) => (
                                                <div>
                                                    <PhoneInput
                                                        inputProps={{
                                                            'data-testid':
                                                                'phone-input',
                                                        }}
                                                        {...fieldProps.input}
                                                        inputClass='sign-up-form-text-input'
                                                        specialLabel={''}
                                                        placeholder={
                                                            localization.phoneNumberPlaceholder
                                                        }
                                                        value={values.phone}
                                                    />
                                                    {renderErrorMessage(
                                                        fieldProps.meta
                                                    )}
                                                </div>
                                            )}
                                        </Field>
                                    </SusyFormField>
                                </Col>
                            </Row>

                            {/* Current occupation and employer */}
                            <Row>
                                <Col className='display-grid'>
                                    <SusyFormField
                                        required
                                        label={{
                                            fieldIdentifier: `currentOccupation`,
                                            text: localization.labelCurrentOccupation,
                                        }}
                                    >
                                        <Field
                                            name='currentOccupation'
                                            component='input'
                                            type='text'
                                        >
                                            {(fieldProps) => (
                                                <div>
                                                    <input
                                                        data-testid='occupation-input'
                                                        className='form-control'
                                                        {...fieldProps.input}
                                                        placeholder={
                                                            localization.currentOccupationPlaceholder
                                                        }
                                                    />
                                                    {renderErrorMessage(
                                                        fieldProps.meta
                                                    )}
                                                </div>
                                            )}
                                        </Field>
                                    </SusyFormField>
                                </Col>
                                <Col className='display-grid'>
                                    <SusyFormField
                                        required
                                        label={{
                                            fieldIdentifier: `employer`,
                                            text: localization.labelEmployer,
                                        }}
                                    >
                                        <Field
                                            name='employer'
                                            component='input'
                                            type='text'
                                        >
                                            {(fieldProps) => (
                                                <div>
                                                    <input
                                                        data-testid='employer-input'
                                                        className='form-control'
                                                        placeholder={
                                                            localization.employerPlaceholder
                                                        }
                                                        {...fieldProps.input}
                                                    />
                                                    {renderErrorMessage(
                                                        fieldProps.meta
                                                    )}
                                                </div>
                                            )}
                                        </Field>
                                    </SusyFormField>
                                </Col>
                            </Row>

                            {/* Academic degree*/}
                            <Row className='susy-mt-4'>
                                <Col xs={6}>
                                    <SusyFormField
                                        required
                                        label={{
                                            fieldIdentifier: `academicDegree`,
                                            text: localization.labelAcademicDegree,
                                        }}
                                    >
                                        <Field name='academicDegree'>
                                            {(fieldProps) => (
                                                <React.Fragment>
                                                    <AppSelect
                                                        {...fieldProps.input}
                                                        options={getAcademicDegreeOptions()}
                                                        testId='academic-degree-select'
                                                    />
                                                    {renderErrorMessage(
                                                        fieldProps.meta
                                                    )}
                                                </React.Fragment>
                                            )}
                                        </Field>
                                    </SusyFormField>
                                </Col>
                            </Row>

                            {/* Degree confirmation checkbox */}
                            <Row className='susy-mb-1'>
                                <Col className='display-grid' xs={12}>
                                    <Field
                                        name='degreeConfirmation'
                                        type='checkbox'
                                    >
                                        {(fieldProps) => (
                                            <>
                                                <SusyCheckbox
                                                    {...fieldProps.input}
                                                    label={
                                                        localization.labelDegreeConfirmation
                                                    }
                                                    dataTestId='degree-confirmation-checkbox'
                                                    fieldIdentifier='degreeConfirmation'
                                                />
                                                {renderErrorMessage(
                                                    fieldProps.meta
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </Col>
                            </Row>

                            {/* Academic degree file proof */}
                            <Row>
                                <Col className='display-grid'>
                                    <SusyFormField
                                        label={{
                                            fieldIdentifier: `academicDegreeProof`,
                                            text: localization.labelAcademicDegreeProof,
                                        }}
                                    >
                                        <Field name='academicDegreeProof'>
                                            {() => (
                                                <React.Fragment>
                                                    <UploadFile
                                                        label=''
                                                        file={null}
                                                        fileUrl=''
                                                        files={setProofFile()}
                                                        accept={acceptPdf}
                                                        onDrop={onDrop}
                                                        fieldName='academicDegreeProof'
                                                        onRemove={onRemove}
                                                        pictureClass='id-picture'
                                                        multiple={false}
                                                        showFileList={true}
                                                        isRequired={false}
                                                        degreeType
                                                        maxSize={
                                                            ACADEMIC_DEGREE_PROOF_FILE_SIZE_LIMIT
                                                        }
                                                        type={
                                                            DropZoneType.default
                                                        }
                                                        hidePresentationFile
                                                    />
                                                </React.Fragment>
                                            )}
                                        </Field>
                                    </SusyFormField>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <div className='susy-body-2-paragraph sign-up-form-data-protection-info'>
                                        <span>
                                            {localization.textConfirm}
                                            <span
                                                className='sign-up-form-link'
                                                onClick={() => {
                                                    downloadFile(
                                                        generatePublicFileUrl(
                                                            localization.fileOriginalName
                                                        ),
                                                        localization.fileOriginalName
                                                    );
                                                }}
                                            >
                                                {
                                                    localization.dataProtectionInformation
                                                }
                                            </span>
                                        </span>
                                    </div>
                                    <div className='d-flex justify-content-center'>
                                        <SusyButton
                                            text={
                                                localization.buttonConfirmAndSend
                                            }
                                            classes='mt-3'
                                            htmlAttributes={{ type: 'submit' }}
                                            dataTestId='submit-sign-up-button'
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    )}
                />
            </AuthPageWrapper>
        );
    };

    const renderSignUpCompleteMessage = () => {
        return (
            <AuthPageWrapper
                title={localization.pendingInvitationTitle}
                subtitle={localization.pendingInvitationSubtitle}
            />
        );
    };

    return isSignUpCompleted ? renderSignUpCompleteMessage() : renderForm();
};

interface SelectAcademicDegreeOption {
    label: string;
    value: string;
}

interface FormValues {
    firstName: string;
    lastName: string;
    eMail: string;
    phone: string;
    currentOccupation: string;
    employer: string;
    academicDegree: SelectAcademicDegreeOption;
    academicDegreeProof: any;
    degreeConfirmation: boolean;
}

interface FormValueErrors {
    firstName: string | undefined;
    lastName: string | undefined;
    eMail: string | undefined;
    phone: string | undefined;
    currentOccupation: string | undefined;
    employer: string | undefined;
    academicDegree: string | undefined;
    degreeConfirmation: string | undefined;
}
